import React, { useEffect, useState, useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import { retrieveHareKrishnaInventoryData, getXlsProductDetails, uploadFile, updateQuantity } from "../../api/PupilApi";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toWords } from "number-to-words";
import { saveOrUpdateInvoiceData } from "../../api/PupilApi";
import toast, { Toaster } from "react-hot-toast";
import { message } from "antd";
import { stringify } from "uuid";

const InventoryDataTab = () => {
  const [loading, setLoading] = useState(true);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [orgData, setOrgData] = useState(null);
  const [sheetProducts, setSheetProducts] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Invoice form state
  const [lineManagerEmail, setLineManagerEmail] = useState("");
  const [shopEmail, setShopEmail] = useState("");
  const [shopContactNo, setShopContactNo] = useState("");
  const [shopState, setShopState] = useState("");
  const [shopGst, setShopGst] = useState("");
  const [shopName, setShopName] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [logoCategory, setLogoCategory] = useState("kiwi");
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [shippingAddress, setShippingAddress] = useState("");
  const [received, setReceived] = useState(0);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");

  // 1. Declare function outside useEffect so it's reusable
  const fetchOrgData = async () => {
    setLoading(true);
    try {
      const response = await retrieveHareKrishnaInventoryData();
      if (response?.data?.success) {
        setOrgData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching organization data:", error);
      toast.error("Failed to load organization data");
    } finally {
      setLoading(false);
    }
  };

  // 2. Run it once when the component mounts
  useEffect(() => {
    fetchOrgData();
  }, []);


  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError("");
  };

  useEffect(() => {
    setError("");
  }, [file]);



  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadFile(formData);
      message.success(res.data.message);
      setFile(null);
      document.querySelector('input[type="file"]').value = null;
      await fetchOrgData();
    } catch (err) {
      setError("Upload failed: " + (err.response?.data?.message || err.message));
    }
  };

  const fetchSheetProducts = async (sheetName) => {
    if (!sheetName) return;

    setProductsLoading(true);
    try {
      const response = await getXlsProductDetails(sheetName);
      if (response?.data?.success) {
        const productsWithSheetName = (response.data.data || []).filter(product => product.remainingQuantity !== 0).map(product => ({
          ...product,
          sheetName, // explicitly attach sheetName to each product
        }));
        setSheetProducts(productsWithSheetName);
      } else {
        setSheetProducts([]);
        toast.error(response?.data?.message || "No products found for this sheet");
      }
    } catch (error) {
      console.error("Error fetching sheet products:", error);
      toast.error("Failed to load products");
      setSheetProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };


  const handleSheetSelect = (e) => {
    const newSheet = e.target.value;
    setSelectedSheet(newSheet);
  };

  const handleLoadProducts = () => {
    if (!selectedSheet) {
      toast.error("Please select a sheet first");
      return;
    }
    fetchSheetProducts(selectedSheet);
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.productId === product.productId);
      if (isSelected) {
        return prev.filter(p => p.productId !== product.productId);
      } else {
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  };

  const handleAddToInvoice = () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    setInvoiceItems(prevItems => {
      // Create a map of existing items for quick lookup
      const existingItemsMap = new Map(prevItems.map(item => [item.productId, item]));

      // Merge new items with existing ones
      const mergedItems = selectedProducts.map(product => {
        const existingItem = existingItemsMap.get(product.productId);
        return existingItem || {
          ...product,
          sheetName: product.sheetName,
          pricePerUnit: (product.mrpPerPcs * (1 - (product.dealerPercentage / 100))) ?? 0,
          gstPercentage: product.dealerGstPercentage ?? 18,
          dealerPercentage: product.dealerPercentage ?? 0,
          quantity: 1,
          discount: product.dealerPercentage ?? 0,
          remainingQuantity: product.remainingQuantity ?? 0,
        };
      });

      // Also keep any existing items that weren't in the new selection
      const otherExistingItems = prevItems.filter(
        item => !selectedProducts.some(p => p.productId === item.productId)
      );

      return [...otherExistingItems, ...mergedItems];
    });

    setShowInvoiceForm(true);
    setSelectedProducts([]);
  };

  const handleQuantityChange = (productId, quantity) => {
    setInvoiceItems(prevItems =>
      prevItems.map(item =>
        item.productId === productId
          ? { ...item, quantity }
          : item
      )
    );
  };


  const handleRemoveItem = (productId) => {
    setInvoiceItems(prevItems =>
      prevItems.filter(item => item.productId !== productId)
    );
  };

  const handleSaveInvoice = async () => {
    if (!orgData?.invoiceNumber) {
      toast.error("Invoice number is not available");
      return;
    }

    if (invoiceItems.length === 0) {
      toast.error("Please add at least one item to the invoice");
      return;
    }

    if (!shopEmail || !lineManagerEmail) {
      toast.error("Please select both shop and line manager");
      return;
    }

    try {
      const totalAmount = invoiceItems.reduce(
        (sum, item) => sum + (item.quantity * item.pricePerUnit),
        0
      );


      const sheetMap = new Map();
      for (const item of invoiceItems) {
        if (!sheetMap.has(item.sheetName)) {
          sheetMap.set(item.sheetName, []);
        }
        sheetMap.get(item.sheetName).push({
          itemId: item.productId,
          productName: item.productType,
          category: item.category || '',
          stockType: item.productType || '',
          totalQuantity: item.totalQuantity || 0,
          dealerCostExGst: (item.dealerCostExGst) || 0,
          quantity: item.quantity,
          pricePerUnit: item.pricePerUnit ?? 0,
          gst: item.dealerGstPercentage,
          unit: item.unit,
          discount: item.discount ?? item.dealerPercentage,
          mrpPerPcs: item.mrpPerPcs,
          mrp: item.mrp || "",
          amount: (item.quantity * item.pricePerUnit).toFixed(2),
        });
      }

      const updateQuantityInvoiceItem = Array.from(sheetMap.entries()).map(([sheetName, items]) => ({
        sheetName,
        items
      }));

      const invoicePayload = {
        invoiceNo: orgData.invoiceNumber,
        date: invoiceDate,
        lineManagerEmail,
        shopName,
        billingAddress,
        shopContactNo,
        shopEmail,
        shopState,
        shopGst,
        received: received.toString(),
        shippingAddress,
        logoCategory,
        totalAmount: totalAmount.toFixed(2),
        updateQuantityInvoiceItem,
      };


      const response = await saveOrUpdateInvoiceData(invoicePayload);
      if (response.data.success) {
        toast.success(response.data.message);
        generateInvoicePDF(invoicePayload);
        setInvoiceItems([]);
        setShowInvoiceForm(false);
        await fetchOrgData();
        handleLoadProducts();

      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.message || "Failed to save invoice");
    }

  };

  const generateInvoicePDF = (invoiceData) => {
    const doc = new jsPDF();

    const margin = 8;
    const pageWidth = doc.internal.pageSize.width;
    const centerX = pageWidth / 2;

    const formatDate = (inputDate) => {
      if (!inputDate) return "N/A";
      const [year, month, day] = inputDate.split("-"); // YYYY-MM-DD
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day}-${monthNames[parseInt(month) - 1]}-${year}`;
    };

    const formattedDate = formatDate(invoiceData?.date);

    // Header
    doc.setFont("helvetica", "bold").setFontSize(14);
    doc.text("Hare Krishna Traders".toUpperCase(), centerX, 13, { align: "center" });

    const addressLines = [
      "12/22, Koothanur, Mallikundam Road, Mecheri, Salem 636458",
      "Phone: 9787292119, 9789573268, 9790168814 | Email: jayaprakash892@gmail.com",
      "GSTIN: 33CFYPM6838M1ZF | State: TamilNadu (33)"
    ];

    let addressY = 20;
    addressLines.forEach(line => {
      doc.setFont("helvetica", "normal").setFontSize(8);
      doc.text(line, centerX, addressY, { align: "center" });
      addressY += 5;
    });

    doc.setDrawColor(150);
    doc.line(margin, addressY + 1, pageWidth - margin, addressY + 1);
    doc.setFontSize(12).setTextColor(0, 0, 128).setFont("helvetica", "bold");
    doc.text("TAX INVOICE", centerX, addressY + 5, { align: "center" });

    // Invoice info
    doc.setFontSize(9).setTextColor(0, 102, 51);
    doc.text("Invoice Details:", 130, addressY + 10);
    doc.setFontSize(8).setTextColor(0);
    doc.text(`Invoice No: ${invoiceData?.invoiceNo || "N/A"}`, 130, addressY + 15);
    doc.text(`Date: ${formattedDate}`, 130, addressY + 20);
    doc.text(`Place of Supply: TamilNadu (33)`, 130, addressY + 25);

    // Bill To
    doc.setFontSize(9).setTextColor(0, 102, 51);
    doc.text("Bill To:", margin, addressY + 10);

    let billToY = addressY + 15;
    doc.setFont("helvetica", "bold").setFontSize(9);
    doc.text(invoiceData?.shopName?.toUpperCase() || "N/A", margin, billToY);
    billToY += 5;

    doc.setFont("helvetica", "normal").setFontSize(8);

    const billingLines = (invoiceData?.billingAddress || "N/A").split('\n');
    billingLines.forEach(line => {
      doc.text(line, margin, billToY);
      billToY += 4;
    });

    doc.text(`Contact No: ${invoiceData?.shopContactNo || "N/A"}`, margin, billToY);
    billToY += 4;
    doc.text(`GSTIN Number: ${invoiceData?.shopGst || "N/A"}`, margin, billToY);
    billToY += 4;
    doc.text(`State: TamilNadu (33)`, margin, billToY);


    // Flatten items from nested updateQuantityInvoiceItem
    const items = invoiceData?.updateQuantityInvoiceItem?.flatMap(sheet =>
      sheet.items.map(item => ({ ...item, sheetName: sheet.sheetName }))
    ) || [];

    const totals = items.reduce((acc, item) => {
      const qty = parseFloat(item.quantity ?? 0);
      const mrp = parseFloat(item.mrp ?? 0);
      const discount = item.discount ?? 0;
      const price = parseFloat(item.mrpPerPcs ?? 0);
      const pcs = parseFloat(qty / (mrp / price) ?? 0);
      const gst = parseFloat((item.gst) ?? 0);
      acc.totalpcs += isNaN(pcs) ? 0 : pcs;
      acc.totalAmount += (qty * price) * (1 - (discount / 100));
      acc.price += (qty * (price * (1 - (discount / 100))) * 100) / (100 + gst);
      acc.totalGST += ((qty * price) * (1 - (discount / 100))) - ((qty * (price * (1 - (discount / 100))) * 100) / (100 + gst));
      return acc;
    }, { totalQuantity: 0, totalGST: 0, price: 0, totalAmount: 0 , totalpcs: 0});

    const productCount = items.length;
    const dynamicCellPadding = productCount > 25 ? 1 : 1.5;
    const fontsizeadjust = productCount > 25 ? 7 : 8;

    // Main Item Table
    doc.autoTable({
      head: [["S.NO", "Product Name", "MRP", "Qty", "pcs", "Unit", "Price/Unit", "GST/Unit", "Discount", "Price", "GST Amt", "Total"]],
      body: [
        ...items.map((item, index) => {
          const qty = item.quantity ?? 0;
          const mrp = item.mrp;
          const unit = item.unit;
          const discount = item.discount ?? 0;
          const unitPrice = item.mrpPerPcs ?? 0;
          const pcs = item.quantity / (item.mrp / item.mrpPerPcs) ?? 0;
          const gst = item.gst ?? 0;
          const discountedPrice = unitPrice * (1 - (discount / 100));
          const unitGst = discountedPrice - (((discountedPrice) * 100) / (100 + gst));
          const noGstPrice = ((discountedPrice) * 100) / (100 + gst);
          const price = qty * ((discountedPrice) * 100) / (100 + gst);
          const total = qty * discountedPrice;
          const gstAmt = (qty * discountedPrice) - (qty * ((discountedPrice) * 100) / (100 + gst));

          return [
            index + 1,
            item.productName || "-",
            mrp, // MRP not in input 
            pcs.toFixed(2),
            qty,
            unit, // default unit
            noGstPrice.toFixed(2),
            (unitGst).toFixed(2),
            discount, // discount placeholder
            price.toFixed(2),
            gstAmt.toFixed(2),
            total.toFixed(2)
          ];
        }),
        [
          { content: "TOTAL", styles: { fontStyle: "bold", fillColor: [255, 204, 0] } },
          "",
          "",
          totals.totalpcs,
          totals.totalQuantity,
          "",
          "",
          "",
          "",
          totals.price.toFixed(2),
          totals.totalGST.toFixed(2),
          totals.totalAmount.toFixed(2)
        ]
      ],
      startY: billToY + 6,
      theme: "grid",
      styles: {
        fontSize: fontsizeadjust,
        cellPadding: dynamicCellPadding,
        fontStyle: "bold",
        tableLineColor: [0, 0, 0],
        tableLineWidth: 1
      },
      headStyles: {
        fillColor: [0, 102, 51],
        textColor: [255, 255, 255],
      },
      margin: 8,
      pageBreak: "auto"
    });

    let finalY = doc.lastAutoTable.finalY + 0;

    const summaryData = [
      ["Subtotal Price", totals.price.toFixed(2)],
      ["SGST @9%", (totals.totalGST / 2).toFixed(2)],
      ["CGST @9%", (totals.totalGST / 2).toFixed(2)],
      ["Rounded Off Total", Math.round(totals.totalAmount).toFixed(2)],
      [
        { content: "TOTAL", styles: { fontStyle: "bold", fillColor: [255, 204, 0], halign: "center" } },
        { content: totals.totalAmount.toFixed(2), styles: { fontStyle: "bold", fillColor: [255, 204, 0], halign: "right" } }
      ]
    ];

    doc.autoTable({
      startY: finalY,
      head: [["Description", "Amount[Rs]"]],
      body: summaryData,
      theme: "grid",
      styles: {
        fontSize: fontsizeadjust,
        cellPadding: dynamicCellPadding,
        textColor: [0, 0, 0],
        fontStyle: "bold"
      },
      headStyles: {
        fillColor: [0, 102, 51],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 30, halign: "left" },
        1: { cellWidth: 25, halign: "right" },
      },
      margin: { left: 147 },
      tableWidth: 35,
    });

    let totalAmountWords = toWords(Math.round(totals.totalAmount)).toLowerCase() + " only";
    doc.setFontSize(9).setTextColor(0, 0, 0);
    doc.text("Invoice Amount In Words:", margin, doc.lastAutoTable.finalY - 28);
    doc.setFontSize(9).setTextColor(0, 102, 51);
    doc.text(totalAmountWords, margin, doc.lastAutoTable.finalY - 22);
    doc.setFontSize(9).setTextColor(0, 0, 0);
    doc.text("Terms and Conditions", margin, doc.lastAutoTable.finalY - 16);
    doc.setFontSize(9).setTextColor(127, 0, 255);
    doc.text("Thanks for doing business with us", margin, doc.lastAutoTable.finalY - 8);

    if (invoiceData?.shippingAddress) {
      doc.setFontSize(10).setTextColor(0, 102, 51);
      doc.text("Shipping Address", margin, doc.lastAutoTable.finalY - 0);
      doc.setFontSize(9).setTextColor(0, 0, 0);
      doc.text(invoiceData.shippingAddress, margin, doc.lastAutoTable.finalY + 8);
    }

    let footerY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("For: HARE KRISHNA TRADERS", 150, footerY);

    doc.setFont("helvetica", "bold").text("Authorized Signatory", pageWidth - 15, footerY + 20, { align: "right" });
    doc.setLineWidth(0.5).line(pageWidth - 60, footerY + 13, pageWidth - 13, footerY + 13);

    doc.save(`Invoice_${invoiceData.invoiceNo || "N/A"}.pdf`);
  };




  const handleLineManagerEmailChange = (e) => {
    setLineManagerEmail(e.target.value);
  };

  const handleShopEmailChange = (e) => {
    const email = e.target.value;
    setShopEmail(email);

    if (orgData?.shopClientDataList) {
      const shop = orgData.shopClientDataList.find(s => s.shopEmail === email);
      if (shop) {
        setShopName(shop.shopName || "");
        setShopContactNo(shop.shopContactNo || "");
        setShopState(shop.shopState || "");
        setShopGst(shop.shopGst || "");
        setBillingAddress(shop.billingAddress || "");
      }
    }
  };

  const columns = useMemo(() => [
    {
      Header: "Select",
      accessor: "select",
      Cell: ({ row }) => {
        const product = row.original;
        const isSelected = selectedProducts.some(p => p.productId === product.productId);

        return (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleProductSelection(product)}
            className="h-4 w-4"
          />
        );
      },
    },
    { Header: "Type", accessor: "productType" },
    { Header: "Category", accessor: "category" },
    { Header: "Brand", accessor: "purchaseCompany" },
    { Header: "Total Quantity", accessor: "totalQuantity" },
    { Header: "Sold Quantity", accessor: "soldQuantity" },
    { Header: "Available", accessor: "remainingQuantity" },
    { Header: "HRT Price", accessor: "hktPrice" },
    { Header: "MRP Per Pcs", accessor: "mrpPerPcs" },
    { Header: "MRP price", accessor: "mrp" },
    { Header: "GST %", accessor: "dealerGstPercentage" },
  ], [selectedProducts]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({
    columns,
    data: sheetProducts
  }, useSortBy);

  return (
    <div className="p-4">
      <Toaster position="bottom-right" />

      {/* Header Section */}
      <div className="mb-6 flex flex-wrap gap-4 justify-between">
        <div className="flex gap-4">

          <button
            onClick={handleAddToInvoice}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
            disabled={selectedProducts.length === 0}
          >
            Add to Invoice
          </button>
          <button
            onClick={() => setShowInvoiceForm(!showInvoiceForm)}
            className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
            disabled={invoiceItems.length === 0 && !showInvoiceForm}
          >
            {showInvoiceForm ? "Hide Invoice Form" : "Show Invoice Form"}
          </button>
        </div>
        <Link to="/">
          <button className="bg-slate-500 text-white px-6 py-2 rounded-md hover:bg-slate-700">
            Back
          </button>
        </Link>
        <div className="ml-auto flex items-center gap-4 w-full sm:w-auto">
          <input
            type="file"
            accept=".xlsx,.xls"
            className="border border-gray-300 rounded px-2 py-1"
            onChange={handleFileChange}
          />

          <button
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 w-full sm:w-auto"
            onClick={handleUpload}
          >
            Upload
          </button>
        </div>
      </div>

      {/* Sheet Selection */}
      <div className="mb-4 bg-white p-4 rounded shadow">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium mb-1">Select Sheet:</label>
            <select
              value={selectedSheet}
              onChange={handleSheetSelect}
              className="w-full border p-2 rounded"
              disabled={loading}
            >
              <option value="">-- Select Sheet --</option>
              {orgData?.inputInvoices?.map((sheet, index) => (
                <option key={index} value={sheet.sheetName}>
                  {sheet.sheetName} {sheet.invoiceNo ? `(Invoice: ${sheet.invoiceNo})` : ''}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleLoadProducts}
            disabled={!selectedSheet || productsLoading}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
          >
            {productsLoading ? "Loading..." : "Load Products"}
          </button>
        </div>
      </div>

      {/* Selected Products Preview */}
      {selectedProducts.length > 0 && (
        <div className="mb-4 bg-white p-4 rounded shadow">
          <h3 className="font-bold mb-2">Selected Products ({selectedProducts.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead>
                <tr>
                  <th className="px-4 py-2 border bg-gray-100">Type</th>
                  <th className="px-4 py-2 border bg-gray-100">Category</th>
                  <th className="px-4 py-2 border bg-gray-100">Brand</th>
                  <th className="px-4 py-2 border bg-gray-100">Total Quantity</th>
                  <th className="px-4 py-2 border bg-gray-100">sold Quantity</th>
                  <th className="px-4 py-2 border bg-gray-100">Available</th>
                  <th className="px-4 py-2 border bg-gray-100">HKT Price </th>
                  <th className="px-4 py-2 border bg-gray-100">Dealer Price</th>
                  <th className="px-4 py-2 border bg-gray-100">MRP Price</th>
                  <th className="px-4 py-2 border bg-gray-100">GST %</th>
                  <th className="px-4 py-2 border bg-gray-100">Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedProducts.map((product) => (
                  <tr key={product.productId}>
                    <td className="border px-4 py-2">{product.productType}</td>
                    <td className="border px-4 py-2">{product.category}</td>
                    <td className="border px-4 py-2">{product.purchaseCompany}</td>
                    <td className="border px-4 py-2">{product.totalQuantity}</td>
                    <td className="border px-4 py-2">{product.soldQuantity}</td>
                    <td className="border px-4 py-2">{product.remainingQuantity}</td>
                    <td className="border px-4 py-2">{product.hktPrice}</td>
                    <td className="border px-4 py-2">{product.dealerCostExGst}</td>
                    <td className="border px-4 py-2">{product.mrp}</td>
                    <td className="border px-4 py-2">{product.dealerGstPercentage}</td>
                    <td className="border px-4 py-2">
                      <button
                        onClick={() => toggleProductSelection(product)}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Product Table */}
        <div className={`${showInvoiceForm ? 'lg:w-2/3' : 'w-full'}`}>
          <div className="bg-white rounded shadow p-4">
            {productsLoading ? (
              <div className="text-center py-8">Loading products...</div>
            ) : sheetProducts.length > 0 ? (
              <div className="overflow-x-auto">
                <table {...getTableProps()} className="min-w-full border">
                  <thead>
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                          <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className="px-4 py-2 border bg-gray-100"
                          >
                            {column.render("Header")}
                            {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows.map(row => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map(cell => (
                            <td {...cell.getCellProps()} className="border px-4 py-2">
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {selectedSheet ? "No products found for this sheet" : "Please select a sheet and load products"}
              </div>
            )}
          </div>
        </div>

        {/* Invoice Form */}
        {showInvoiceForm && (
          <div className="lg:w-1/3">
            <div className="bg-white rounded shadow p-4 sticky top-4">
              <h3 className="text-lg font-bold mb-4">Invoice Details</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="w-full border p-2 rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Line Manager</label>
                  <select
                    value={lineManagerEmail}
                    onChange={handleLineManagerEmailChange}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select Line Manager</option>
                    {orgData?.lineManagerDataList?.map((manager, index) => (
                      manager.lineManagerEmail && (
                        <option key={index} value={manager.lineManagerEmail}>
                          {manager.lineManagerEmail}
                        </option>
                      )
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Shop</label>
                  <select
                    value={shopEmail}
                    onChange={handleShopEmailChange}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select Shop</option>
                    {orgData?.shopClientDataList?.map((shop, index) => (
                      shop.shopEmail && (
                        <option key={index} value={shop.shopEmail}>
                          {shop.shopEmail}
                        </option>
                      )
                    ))}
                  </select>
                </div>

                {shopEmail && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Shop Name</label>
                      <input
                        type="text"
                        value={shopName}
                        onChange={(e) => setShopName(e.target.value)}
                        className="w-full border p-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Contact No</label>
                      <input
                        type="text"
                        value={shopContactNo}
                        onChange={(e) => setShopContactNo(e.target.value)}
                        className="w-full border p-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">GST Number</label>
                      <input
                        type="text"
                        value={shopGst}
                        onChange={(e) => setShopGst(e.target.value)}
                        className="w-full border p-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">State</label>
                      <input
                        type="text"
                        value={shopState}
                        onChange={(e) => setShopState(e.target.value)}
                        className="w-full border p-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Billing Address</label>
                      <textarea
                        value={billingAddress}
                        onChange={(e) => setBillingAddress(e.target.value)}
                        className="w-full border p-2 rounded"
                        rows="3"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Shipping Address</label>
                  <textarea
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    className="w-full border p-2 rounded"
                    rows="3"
                  />
                </div>

                <div className="pt-4">
                  <h4 className="font-bold mb-2">Invoice Items ({invoiceItems.length})</h4>
                  <div className="max-h-60 overflow-y-auto border rounded">
                    {invoiceItems.length > 0 ? (
                      invoiceItems.map((item) => (
                        <div key={item.productId} className="border-b p-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium truncate">{item.productType} | {item.category}</div>
                              <div className="text-sm text-gray-600">
                                Available: {item.remainingQuantity - item.quantity} | Sheet: {item.sheetName}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.productId)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </div>

                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center justify-start">

                              {/* Price/Unit */}
                              <div className="flex items-center ml-4">
                                <label className="text-sm mr-2">Price/Unit:</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.pricePerUnit}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;

                                    if (inputValue === "") {
                                      setInvoiceItems(prevItems =>
                                        prevItems.map(i =>
                                          i.productId === item.productId
                                            ? { ...i, pricePerUnit: "" }
                                            : i
                                        )
                                      );
                                      return;
                                    }

                                    if (!/^\d*\.?\d*$/.test(inputValue)) return;

                                    const numericValue = parseFloat(inputValue);
                                    if (isNaN(numericValue) || numericValue < 0) {
                                      toast.error("Enter a valid price");
                                      return;
                                    }

                                    const discount = parseFloat(
                                      ((1 - numericValue / item.mrpPerPcs) * 100).toFixed(2)
                                    );

                                    setInvoiceItems(prevItems =>
                                      prevItems.map(i =>
                                        i.productId === item.productId
                                          ? {
                                            ...i,
                                            pricePerUnit: numericValue,
                                            discount: Math.max(0, Math.min(discount, 100)),
                                          }
                                          : i
                                      )
                                    );
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === "") {
                                      setInvoiceItems(prevItems =>
                                        prevItems.map(i =>
                                          i.productId === item.productId
                                            ? {
                                              ...i,
                                              pricePerUnit: item.mrpPerPcs,
                                              discount: 0,
                                            }
                                            : i
                                        )
                                      );
                                    }
                                  }}
                                  className="w-20 border p-1 text-sm"
                                />
                              </div>

                              {/* Discount */}
                              <div className="flex items-center ml-4">
                                <label className="text-sm mr-2">Discount:</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={item.discount}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;

                                    if (inputValue === "") {
                                      setInvoiceItems(prevItems =>
                                        prevItems.map(i =>
                                          i.productId === item.productId
                                            ? { ...i, discount: "" }
                                            : i
                                        )
                                      );
                                      return;
                                    }

                                    if (!/^\d{1,3}(\.\d{0,2})?$/.test(inputValue)) return;

                                    const numericValue = parseFloat(inputValue);

                                    if (numericValue < 0 || numericValue > 100) {
                                      toast.error("Discount must be between 0% and 100%");
                                      return;
                                    }

                                    const discountedPrice = parseFloat(
                                      (item.mrpPerPcs * (1 - numericValue / 100)).toFixed(2)
                                    );

                                    setInvoiceItems(prevItems =>
                                      prevItems.map(i =>
                                        i.productId === item.productId
                                          ? {
                                            ...i,
                                            discount: numericValue,
                                            pricePerUnit: discountedPrice,
                                          }
                                          : i
                                      )
                                    );
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === "") {
                                      setInvoiceItems(prevItems =>
                                        prevItems.map(i =>
                                          i.productId === item.productId
                                            ? {
                                              ...i,
                                              discount: 0,
                                              pricePerUnit: item.mrpPerPcs,
                                            }
                                            : i
                                        )
                                      );
                                    }
                                  }}
                                  className="w-16 border p-1 text-sm"
                                />
                              </div>
                            </div>



                            <div className="flex flex-col items-end">
                              <div className="flex items-center">
                                <label className="text-sm mr-2">Qty:</label>

                                {/* Packet input */}
                                <input
                                  type="number"
                                  min="0"
                                  className="w-14 border p-1 text-sm mr-1"
                                  placeholder="Packets"
                                  value={item.packets || 0}
                                  step="1"
                                  onChange={(e) => {
                                    const packetValue = parseInt(e.target.value || "0", 10);
                                    const total = packetValue * item.pcsInBox + (item.pieces || 0);

                                    if (total > item.remainingQuantity) {
                                      toast.error(`Total quantity exceeds ${item.remainingQuantity}`);
                                      return;
                                    }

                                    setInvoiceItems(prevItems =>
                                      prevItems.map(i =>
                                        i.productId === item.productId
                                          ? {
                                            ...i,
                                            packets: packetValue,
                                            quantity: total,
                                          }
                                          : i
                                      )
                                    );
                                  }}
                                />

                                {/* Pieces input */}
                                <input
                                  type="number"
                                  min="0"
                                  className="w-14 border p-1 text-sm ml-1"
                                  placeholder="Pieces"
                                  value={item.pieces || 0}
                                  step="1"
                                  onChange={(e) => {
                                    const piecesValue = parseInt(e.target.value || "0", 10);
                                    const total = (item.packets || 0) * item.pcsInBox + piecesValue;

                                    if (total > item.remainingQuantity) {
                                      toast.error(`Total quantity exceeds ${item.remainingQuantity}`);
                                      return;
                                    }

                                    setInvoiceItems(prevItems =>
                                      prevItems.map(i =>
                                        i.productId === item.productId
                                          ? {
                                            ...i,
                                            pieces: piecesValue,
                                            quantity: total,
                                          }
                                          : i
                                      )
                                    );
                                  }}
                                />
                              </div>

                              {/* Label showing total quantity */}
                              <span className="text-xs text-gray-500 mt-1">
                                Total Qty: {item.quantity} = {(item.packets || 0)} packets + {(item.pieces || 0)} pieces
                              </span>
                            </div>





                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500 text-center">No items added to invoice</div>
                    )}
                  </div>
                </div>

                {invoiceItems.length > 0 && (
                  <div className="pt-2">
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Subtotal:</span>
                      <span>
                        ₹{invoiceItems.reduce((sum, item) => sum + (item.quantity * ((item.pricePerUnit * 100) / (100 + item.gstPercentage))), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>GST:</span>
                      <span>
                        ₹{invoiceItems.reduce((sum, item) => sum + ((item.pricePerUnit * item.quantity) - (item.quantity * ((item.pricePerUnit * 100) / (100 + item.gstPercentage)))), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Total:</span>
                      <span>
                        ₹{invoiceItems.reduce((sum, item) => sum + (item.pricePerUnit * item.quantity), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <button
                    onClick={handleSaveInvoice}
                    className={`px-4 py-2 rounded-md flex-1 ${(!shopEmail || !lineManagerEmail || invoiceItems.length === 0)
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 text-white"
                      }`}
                    disabled={!shopEmail || !lineManagerEmail || invoiceItems.length === 0}
                  >
                    Generate Invoice
                  </button>

                  <button
                    onClick={() => setInvoiceItems([])}
                    className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="mt-4 bg-red-100 text-red-700 p-3 rounded">
            ⚠️ {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryDataTab;